import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { Observable, Subscription } from "rxjs";

import {
  BscAvanceCaptura,
  BscEjercicioKpi,
  BscKpiMes,
  BscKpiTrimestre,
  BscRes,
  EstadoMesBsc,
  EstatusTrimestreBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { BscService } from "../bsc.service";
import { ponderado, redondear } from "../bsc-calculos";
import { ESTADO_MES, EstadoBsc } from "../bsc-estados";
import {
  SIN_DATO,
  TOPE_REAL_EN_PORCENTAJE,
  fechaCorta,
  formatoNumero,
  leeNumero,
  leePorcentaje,
  meta,
  mesEnTexto,
  nombreMes,
  numero,
  pct,
  textoNumero,
  textoPorcentaje,
  trimestreDeMes,
  unidad,
} from "../bsc-formato";
import { acumProgEn, acumRealEn, aplicaEn, pesoEn } from "../bsc-kpi";

/** Lo que recibe el cuadro: el KPI, el mes y lo que ya está guardado. */
export interface DatosCapturaMesBsc {
  /**
   * El KPI del año (idEjercicioKpi, objetivo, unidad, meta, pesos y NA, y los
   * acumulados K/L/N del motor para la previa). Es lo único que identifica la
   * captura: planeación, área y empresa las resuelve el servidor.
   */
  kpi: BscEjercicioKpi;
  anio: number | null;
  /** Nombre del área, para el encabezado. */
  area: string;
  /** Mes calendario 1..12 que se captura; el avance es el de su trimestre. */
  mes: number;
  /** El mes como está guardado (con su estado derivado); nulo si no hay renglón. */
  mesGuardado: BscKpiMes | null;
  /** El avance del trimestre del mes como está guardado; nulo sin renglón. */
  avance: BscKpiTrimestre | null;
  /** bsc_ejercicio_trimestre.estatus del trimestre del mes; ENVIADO/AUTORIZADO ya no se capturan. */
  estatusTrimestre: EstatusTrimestreBsc | null;
  /** Hay otro KPI pendiente en el mes: ofrece «Guardar y seguir». */
  haySiguiente?: boolean;
}

/** Cómo se cerró el cuadro. */
export interface ResultadoCapturaMesBsc {
  /** Se guardó algo (el mes, el avance o los dos): quien lo abrió vuelve a consultar. */
  guardado: boolean;
  /**
   * Lo que enseña la pantalla ya no vale (el trimestre se envió, la vía o el
   * año cambiaron, la sesión caducó): quien lo abrió vuelve a consultar.
   */
  recargar: boolean;
  /** Pidió seguir con el siguiente KPI pendiente del mes. */
  siguiente: boolean;
  /** Lo que la pantalla dice al cerrar ("Se guardó septiembre de «…»."); vacío si nada. */
  mensaje: string;
}

/** Un aviso del servidor listo para mostrarse. El código va en el title, no en el texto. */
interface AvisoCaptura {
  codigo: string;
  texto: string;
}

/** Lo que impide mandar, con el campo al que va el foco. */
interface FaltaCaptura {
  texto: string;
  campo: string;
}

const ID_TITULO = "bsc-captura-titulo";
const ID_ACTIVIDAD = "bsc-captura-actividad";
const ID_REAL = "bsc-captura-real";

/** Topes de BscCapturaBuss: comentario 1000 caracteres; los TEXT, 65 535 bytes. */
const LARGO_COMENTARIO = 1000;
const BYTES_TEXT = 65_535;
/** real.do rechaza desde 1 000 000 (fracción) y las unidades desde 10^10. */
const TOPE_PORCENTAJE = 100_000_000;
const TOPE_UNIDADES = 10_000_000_000;

/** `CODIGO[ Qn]: mensaje` (BscAviso.texto()). */
const FORMATO_AVISO = /^([A-Z][A-Z0-9_-]+)(?: (Q[1-4]))?: ([\s\S]+)$/;

/** Lo que deja la pantalla desactualizada: se ofrece recargar en vez de volver a guardar. */
const ERRORES_DE_RECARGA = new Set(["VAL-BSC-CONFLICTO", "VAL-BSC-VIA", "VAL-BSC-CERRADO", "00002", "AUTH-001"]);

/**
 * Abre el cuadro de captura con la forma de los diálogos estándar
 * (app-standard-dialog, como el del PIN). El foco entra a la actividad.
 * Escape y el fondo pasan por cancelar(): con la petición en vuelo no
 * cierran, y con cambios sin guardar piden confirmarlo.
 *
 * @returns el cuadro: afterClosed() da el resultado, o undefined si se
 *          canceló sin guardar nada ni recibir un error que obligue a recargar.
 *          Quien lo abre lo cierra si su pantalla se destruye.
 */
export function abrirCapturaMesBsc(
  dialog: MatDialog,
  datos: DatosCapturaMesBsc,
): MatDialogRef<CapturaMesBscComponent, ResultadoCapturaMesBsc> {
  return dialog.open<CapturaMesBscComponent, DatosCapturaMesBsc, ResultadoCapturaMesBsc>(CapturaMesBscComponent, {
    data: datos,
    width: "760px",
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 32px)",
    panelClass: "app-standard-dialog",
    backdropClass: "app-standard-dialog-backdrop",
    ariaLabelledBy: ID_TITULO,
    autoFocus: `#${ID_ACTIVIDAD}`,
    disableClose: true,
  });
}

/**
 * BSC · captura de un mes y del avance de su trimestre (docs/BSC.md §4 «Mes»
 * y «Concurrencia y seguridad de la captura», §7; F6.4). Lo abren C1 (Mi BSC,
 * desde el checklist) y A3 (la ficha del KPI, «Capturar <mes>»).
 *
 * Dos partes, dos endpoints, en este orden:
 *  - El mes (mes.do): actividad planeada y evidencia/resultado. Un texto
 *    vacío borra lo guardado; el mes no puede quedar sin los dos.
 *  - El avance del trimestre (real.do): real (I) y programado (J) en %, que
 *    viajan como fracción (109 % viaja como 1.09); valor en unidades
 *    (informativo, "2 de 6"), recursos y comentario. Un campo que no se tocó
 *    no viaja (el servidor conserva lo guardado); el real no se puede borrar,
 *    solo corregir. En % el real llega hasta TOPE_REAL_EN_PORCENTAJE.
 * Solo se manda la parte que cambió. Si el mes se guarda y el avance no, el
 * cuadro lo dice y deja corregir el avance sin volver a mandar el mes.
 *
 * Lo que se dice antes de guardar (programado distinto de 100 %, real que
 * supera lo programado, un real menor que 1 % que quizá era un porcentaje)
 * es una previa: el servidor manda y sus avisos (SUPERA_PROGRAMADO,
 * PROGRAMADO_CERO, PROGRAMADO_DISTINTO_DE_1, KPI_NO_APLICA)
 * se enseñan al guardar, con el cuadro abierto hasta que se leen. Un
 * VAL-BSC-CONFLICTO (el trimestre ya se envió o se autorizó) y lo que deja la
 * pantalla desactualizada ofrecen recargar en vez de volver a guardar.
 *
 * En un trimestre NA del KPI el mes se captura (queda registrado con aviso)
 * y del avance solo unidades, recursos y comentario: real y programado no
 * cuentan y el servidor los rechaza.
 */
@Component({
  selector: "app-captura-mes-bsc",
  standalone: true,
  templateUrl: "./captura-mes-bsc.component.html",
  styleUrls: ["../bsc.shared.css", "./captura-mes-bsc.component.css"],
  imports: [CommonModule, FormsModule, MatDialogContent, MatDialogActions, MatFormFieldModule, MatInputModule],
})
export class CapturaMesBscComponent implements OnInit, OnDestroy {
  readonly idTitulo = ID_TITULO;
  readonly idActividad = ID_ACTIVIDAD;
  readonly idReal = ID_REAL;
  readonly largoComentario = LARGO_COMENTARIO;

  /* ---------- Lo fijo del cuadro ---------- */
  readonly q: number;
  readonly mesTexto: string;
  readonly mesNombre: string;
  readonly objetivo: string;
  readonly encabezado: string;
  readonly aplica: boolean;
  /** El trimestre ya se envió o se autorizó: no se captura (el servidor lo rechazaría). */
  readonly bloqueado: boolean;
  readonly enPorcentaje: boolean;
  /** Las unidades se piden en No y días, o si ya traen un valor. */
  readonly conUnidades: boolean;
  readonly estadoMes: EstadoBsc<EstadoMesBsc> | null;
  readonly capturo: string;
  readonly textoNa: string;

  /* ---------- Los campos ---------- */
  actividad = "";
  evidencia = "";
  real = "";
  programado = "";
  unidades = "";
  recursos = "";
  comentario = "";

  /* ---------- Lo que se dice ---------- */
  enviando = false;
  /** Lo que impide guardar o el error del servidor. */
  mensaje = "";
  /** Los avisos del servidor de lo guardado; con ellos el cuadro espera a «Listo». */
  avisos: AvisoCaptura[] = [];
  /** Se guardó todo y hay avisos que leer. */
  terminado = false;
  /** El error deja la pantalla desactualizada: el botón es «Recargar». */
  recargar = false;
  /** Primer Cancelar con cambios: el segundo los descarta. */
  confirmarDescarte = false;

  private readonly inicial = { actividad: "", evidencia: "", real: "", programado: "", unidades: "", recursos: "", comentario: "" };
  private guardoAlgo = false;
  private readonly queSeGuardo: string[] = [];
  private pidioSiguiente = false;
  private readonly subs: Subscription[] = [];
  private cerrado = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly datos: DatosCapturaMesBsc,
    private readonly dialogRef: MatDialogRef<CapturaMesBscComponent, ResultadoCapturaMesBsc>,
    private readonly bscService: BscService,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    const k = datos.kpi;
    this.q = trimestreDeMes(datos.mes);
    this.mesTexto = mesEnTexto(datos.mes);
    this.mesNombre = nombreMes(datos.mes);
    this.objetivo = (k.objetivo ?? "").trim() || "KPI sin objetivo";
    this.aplica = aplicaEn(k, this.q);
    this.bloqueado = datos.estatusTrimestre === "ENVIADO" || datos.estatusTrimestre === "AUTORIZADO";
    this.enPorcentaje = !k.unidad || k.unidad === "%";
    this.conUnidades = !this.enPorcentaje || numero(datos.avance?.valorUnidades) != null;
    const partes = [
      datos.area,
      datos.anio != null ? `${datos.anio}` : "",
      `meta ${meta(k.metaValor, k.metaTexto, k.unidad)}`,
      this.aplica ? `peso Q${this.q} ${pct(pesoEn(k, this.q))}` : `NA en Q${this.q}`,
    ];
    this.encabezado = partes.filter(Boolean).join(" · ");
    this.estadoMes = ESTADO_MES.find((e) => e.valor === datos.mesGuardado?.estado) ?? null;
    const m = datos.mesGuardado;
    const fecha = m?.fechaModificacion || m?.fechaCreacion;
    this.capturo = m?.id != null
      ? `Capturó ${m.usuarioAlta || SIN_DATO}${sufijoFecha(fecha)}`
      : "";
    const inicio = k.trimestreInicio ?? 1;
    this.textoNa = inicio > this.q
      ? `El KPI aplica desde Q${inicio}: en Q${this.q} su avance no cuenta. La actividad del mes sí se registra.`
      : `El KPI no aplica en Q${this.q} (NA): su avance no cuenta. La actividad del mes sí se registra.`;

    // Sin separador "----" el FOR-011 dejó todo el texto del mes en actividad (§6 paso 5).
    const actividad = (m?.actividad ?? "").trim() || (m?.evidencia ? "" : (m?.textoOriginal ?? "").trim());
    const a = datos.avance;
    this.inicial = {
      actividad,
      evidencia: (m?.evidencia ?? "").trim(),
      real: textoPorcentaje(a?.avanceReal),
      programado: textoPorcentaje(a?.avanceProgramado ?? 1),
      unidades: textoNumero(a?.valorUnidades),
      recursos: (a?.recursos ?? "").trim(),
      comentario: (a?.comentario ?? "").trim(),
    };
    this.actividad = this.inicial.actividad;
    this.evidencia = this.inicial.evidencia;
    this.real = this.inicial.real;
    this.programado = this.inicial.programado;
    this.unidades = this.inicial.unidades;
    this.recursos = this.inicial.recursos;
    this.comentario = this.inicial.comentario;
  }

  ngOnInit(): void {
    this.subs.push(
      this.dialogRef.backdropClick().subscribe(() => this.cancelar()),
      this.dialogRef.keydownEvents().subscribe((ev) => {
        if (ev.key === "Escape") {
          ev.preventDefault();
          this.cancelar();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.cerrado = true;
    this.subs.forEach((s) => s.unsubscribe());
  }

  /* ============================ Lo que se lee al escribir ============================ */

  get mesSucio(): boolean {
    return this.actividad.trim() !== this.inicial.actividad || this.evidencia.trim() !== this.inicial.evidencia;
  }

  get avanceSucio(): boolean {
    return (
      (this.aplica && (this.real.trim() !== this.inicial.real || this.programado.trim() !== this.inicial.programado)) ||
      this.unidades.trim() !== this.inicial.unidades ||
      this.recursos.trim() !== this.inicial.recursos ||
      this.comentario.trim() !== this.inicial.comentario
    );
  }

  get sucio(): boolean {
    return this.mesSucio || this.avanceSucio;
  }

  /** Bajo el real: lo que dice el motor de lo que se escribió. */
  get ayudaReal(): string {
    if (!this.aplica) {
      return "No aplica en este trimestre.";
    }
    const r = leePorcentaje(this.real);
    if (r.error) {
      return r.error;
    }
    if (r.valor == null) {
      return this.inicial.real
        ? "Lo capturado no se borra: corrígelo."
        : "Sin capturar. Se escribe en % de lo programado del trimestre.";
    }
    if (r.valor < 0) {
      return "No puede ser negativo.";
    }
    // Lo que Guardar no deja mandar (arriba del tope en %, o demasiado grande en No o días) no promete aviso.
    if (this.realFuera(r.valor)) {
      return this.realFueraDeRango(r.valor);
    }
    if (r.valor > 100) {
      return "Supera lo programado (sobrecumplimiento): se guarda con el aviso SUPERA_PROGRAMADO.";
    }
    if (r.valor > 0 && r.valor < 1) {
      return `Es ${formatoNumero(r.valor)} %: si querías decir ${formatoNumero(r.valor * 100)} %, escribe ${formatoNumero(r.valor * 100)}.`;
    }
    return this.enPorcentaje ? "De lo programado del trimestre." : `Lo logrado de lo programado; las ${unidad(this.datos.kpi.unidad)} van aparte.`;
  }

  /** Bajo el programado: PROGRAMADO_CERO y PROGRAMADO_DISTINTO_DE_1, como el servidor. */
  get ayudaProgramado(): string {
    if (!this.aplica) {
      return "";
    }
    const p = leePorcentaje(this.programado);
    if (p.error) {
      return p.error;
    }
    if (p.valor == null) {
      return "Lo habitual es 100 %.";
    }
    if (p.valor < 0) {
      return "No puede ser negativo.";
    }
    if (p.valor === 0) {
      return "Con 0 el ponderado del trimestre se toma como 0 (aviso PROGRAMADO_CERO).";
    }
    if (p.valor !== 100) {
      return "Distinto de 100 %: se guarda con el aviso PROGRAMADO_DISTINTO_DE_1.";
    }
    return "Lo habitual: 100 %.";
  }

  get avisoProgramado(): boolean {
    const p = leePorcentaje(this.programado);
    return this.aplica && !p.error && p.valor != null && p.valor !== 100;
  }

  get avisoReal(): boolean {
    const r = leePorcentaje(this.real);
    return (
      this.aplica &&
      (!!r.error ||
        (r.valor == null && !!this.inicial.real) ||
        (r.valor != null && (r.valor < 0 || r.valor > 100 || (r.valor > 0 && r.valor < 1))))
    );
  }

  /**
   * La previa de lo que calcularía el motor con lo escrito: K = I × M / J y
   * L = L del trimestre anterior + K, contra N (con un real arriba de 100 %,
   * K y L pasan de lo programado). Vacía si falta el real o no se puede mandar.
   */
  get previa(): string {
    if (!this.aplica) {
      return "";
    }
    const r = leePorcentaje(this.real);
    const p = leePorcentaje(this.programado);
    if (r.error || p.error || r.valor == null || this.realFuera(r.valor)) {
      return "";
    }
    const k = this.datos.kpi;
    const real = redondear(r.valor / 100);
    const prog = p.valor == null ? 1 : redondear(p.valor / 100);
    const kq = ponderado(real, pesoEn(k, this.q), prog) ?? 0;
    const antes = this.q > 1 ? (acumRealEn(k, this.q - 1) ?? 0) : 0;
    const l = redondear(antes + kq);
    return `Ponderado de Q${this.q} (K) ${pct(kq)} · acumulado (L) ${pct(l)} de ${pct(acumProgEn(k, this.q))} programado (N).`;
  }

  /* ============================ Guardar ============================ */

  guardar(siguiente: boolean): void {
    if (this.enviando || this.bloqueado || this.terminado || this.recargar) {
      return;
    }
    this.confirmarDescarte = false;
    this.pidioSiguiente = siguiente;
    if (!this.sucio) {
      this.mensaje = "No hay cambios que guardar.";
      this.pinta();
      return;
    }
    const error = this.revisar();
    if (error) {
      this.mensaje = error.texto;
      this.pinta();
      this.enfoca(error.campo);
      return;
    }
    this.mensaje = "";
    this.enviando = true;
    this.pinta();
    if (this.mesSucio) {
      this.paso(
        this.bscService.capturarMes(this.datos.kpi.idEjercicioKpi as number, this.datos.mes, this.actividad.trim(), this.evidencia.trim()),
        this.mesTexto,
        () => {
          this.inicial.actividad = this.actividad.trim();
          this.inicial.evidencia = this.evidencia.trim();
          if (this.avanceSucio) {
            this.guardarAvance();
          } else {
            this.termina();
          }
        },
      );
    } else {
      this.guardarAvance();
    }
  }

  private guardarAvance(): void {
    const avance = this.avanceAMandar();
    this.paso(
      this.bscService.capturarReal(this.datos.kpi.idEjercicioKpi as number, this.q, avance),
      `el avance de Q${this.q}`,
      () => {
        this.inicial.real = this.real.trim();
        this.inicial.programado = this.programado.trim();
        this.inicial.unidades = this.unidades.trim();
        this.inicial.recursos = this.recursos.trim();
        this.inicial.comentario = this.comentario.trim();
        this.termina();
      },
    );
  }

  /** Una petición: sus avisos se juntan; un error detiene lo que sigue y dice qué sí se guardó. */
  private paso(peticion: Observable<BscRes>, que: string, despues: () => void): void {
    this.subs.push(
      peticion.subscribe({
        next: (res) => {
          if (!res || res.codigoError) {
            this.falla(res, que);
            return;
          }
          this.guardoAlgo = true;
          this.queSeGuardo.push(que);
          this.avisos.push(...(res.avisos ?? []).map(parteAviso));
          despues();
        },
        error: () => this.falla(null, que, true),
      }),
    );
  }

  private termina(): void {
    this.enviando = false;
    if (this.avisos.length === 0) {
      this.cierra(this.pidioSiguiente);
      return;
    }
    // Con avisos el cuadro espera a que se lean.
    this.terminado = true;
    this.pinta();
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(".bsc-captura-dialog__listo")?.focus());
  }

  private falla(res: BscRes | null | undefined, que: string, sinConexion = false): void {
    this.enviando = false;
    const codigo = res?.codigoError ?? "";
    let texto: string;
    if (sinConexion) {
      texto = `Sin conexión con el servidor: no se guardó ${que}.`;
    } else if (codigo === "AUTH-001") {
      texto = "Tu sesión caducó. Vuelve a entrar.";
    } else {
      texto = res?.mensajeError || `No se pudo guardar ${que}.`;
    }
    if (this.queSeGuardo.length > 0) {
      texto = `Se guardó ${this.queSeGuardo.join(" y ")}, pero no ${que}. ${texto}`;
    }
    this.recargar = ERRORES_DE_RECARGA.has(codigo);
    if (codigo === "VAL-BSC-CONFLICTO") {
      texto += " Recarga para ver cómo quedó.";
    }
    this.mensaje = texto;
    this.pinta();
    this.enfoca(this.recargar ? ".bsc-captura-dialog__recargar" : `#${ID_ACTIVIDAD}`);
  }

  /** Lo que impide mandar, con el campo al que va el foco; nulo si se puede. */
  private revisar(): FaltaCaptura | null {
    return this.revisarTextos() ?? this.revisarPorcentajes() ?? this.revisarUnidades();
  }

  /** El mes no puede quedar vacío y los textos tienen tope. */
  private revisarTextos(): FaltaCaptura | null {
    const actividad = this.actividad.trim();
    const evidencia = this.evidencia.trim();
    if (this.mesSucio && !actividad && !evidencia) {
      return { texto: `Escribe la actividad o la evidencia de ${this.mesTexto}: el mes no puede quedar vacío.`, campo: `#${ID_ACTIVIDAD}` };
    }
    for (const [texto, nombre] of [[actividad, "La actividad"], [evidencia, "La evidencia"], [this.recursos.trim(), "Los recursos"]]) {
      if (bytes(texto) > BYTES_TEXT) {
        return { texto: `${nombre} pasa del tamaño permitido (64 KB).`, campo: `#${ID_ACTIVIDAD}` };
      }
    }
    if (this.comentario.trim().length > LARGO_COMENTARIO) {
      return { texto: `El comentario pasa de ${LARGO_COMENTARIO} caracteres.`, campo: "#bsc-captura-comentario" };
    }
    return null;
  }

  /** Real y programado: solo en un trimestre en que el KPI aplica. */
  private revisarPorcentajes(): FaltaCaptura | null {
    if (!this.aplica) {
      return null;
    }
    return this.revisarReal() ?? this.revisarProgramado();
  }

  private revisarReal(): FaltaCaptura | null {
    const r = leePorcentaje(this.real);
    if (r.error) {
      return { texto: `Avance real: ${r.error}`, campo: `#${ID_REAL}` };
    }
    if (r.valor == null && this.inicial.real) {
      return { texto: "El avance real ya capturado no se borra: corrígelo (0 si no hubo avance).", campo: `#${ID_REAL}` };
    }
    if (r.valor != null && this.realFuera(r.valor)) {
      return {
        texto: this.realFueraDeRango(r.valor),
        campo: `#${ID_REAL}`,
      };
    }
    return null;
  }

  /**
   * El real escrito no se puede mandar: negativo, arriba de TOPE_REAL_EN_PORCENTAJE
   * en % (arriba de 100 sí: sobrecumplimiento, con aviso) o demasiado grande.
   */
  private realFuera(valor: number): boolean {
    return valor < 0 || (this.enPorcentaje && valor > TOPE_REAL_EN_PORCENTAJE) || valor >= TOPE_PORCENTAJE;
  }

  /** Por qué no se acepta el real: negativo, más del tope en %, o demasiado grande. */
  private realFueraDeRango(valor: number): string {
    if (valor < 0) {
      return "El avance real no puede ser negativo.";
    }
    return this.enPorcentaje ? `En % el avance real va de 0 a ${TOPE_REAL_EN_PORCENTAJE}.` : "El avance real es demasiado grande.";
  }

  private revisarProgramado(): FaltaCaptura | null {
    const p = leePorcentaje(this.programado);
    if (p.error) {
      return { texto: `Programado: ${p.error}`, campo: "#bsc-captura-programado" };
    }
    if (p.valor == null && this.programado.trim() !== this.inicial.programado) {
      return { texto: "Escribe el programado del trimestre (lo habitual es 100).", campo: "#bsc-captura-programado" };
    }
    if (p.valor != null && (p.valor < 0 || p.valor >= TOPE_PORCENTAJE)) {
      return {
        texto: p.valor < 0 ? "El programado no puede ser negativo." : "El programado es demasiado grande.",
        campo: "#bsc-captura-programado",
      };
    }
    return null;
  }

  private revisarUnidades(): FaltaCaptura | null {
    const u = leeNumero(this.unidades);
    if (u.error) {
      return { texto: `Unidades: ${u.error}`, campo: "#bsc-captura-unidades" };
    }
    if (u.valor == null && this.inicial.unidades) {
      return { texto: "Las unidades ya capturadas no se borran: corrígelas.", campo: "#bsc-captura-unidades" };
    }
    if (u.valor != null && (u.valor < 0 || u.valor >= TOPE_UNIDADES)) {
      return { texto: "Las unidades van de 0 en adelante.", campo: "#bsc-captura-unidades" };
    }
    return null;
  }

  /** Solo lo que cambió: lo que no viaja, el servidor lo conserva. Los textos vacíos borran. */
  private avanceAMandar(): BscAvanceCaptura {
    const avance: BscAvanceCaptura = {};
    if (this.aplica && this.real.trim() !== this.inicial.real) {
      const r = leePorcentaje(this.real).valor;
      avance.avanceReal = r == null ? null : redondear(r / 100);
    }
    if (this.aplica && this.programado.trim() !== this.inicial.programado) {
      const p = leePorcentaje(this.programado).valor;
      avance.avanceProgramado = p == null ? null : redondear(p / 100);
    }
    if (this.unidades.trim() !== this.inicial.unidades) {
      avance.valorUnidades = leeNumero(this.unidades).valor;
    }
    if (this.recursos.trim() !== this.inicial.recursos) {
      avance.recursos = this.recursos.trim();
    }
    if (this.comentario.trim() !== this.inicial.comentario) {
      avance.comentario = this.comentario.trim();
    }
    return avance;
  }

  /* ============================ Cerrar ============================ */

  /** «Listo» o «Seguir» después de leer los avisos. */
  listo(siguiente: boolean): void {
    this.cierra(siguiente);
  }

  /** «Recargar» después de un error que deja la pantalla desactualizada. */
  recargarPantalla(): void {
    this.cierra(false);
  }

  cancelar(): void {
    if (this.enviando || this.cerrado) {
      return;
    }
    if (this.terminado) {
      this.cierra(false);
      return;
    }
    if (this.sucio && !this.recargar && !this.confirmarDescarte) {
      this.confirmarDescarte = true;
      this.mensaje = "Tienes cambios sin guardar. Vuelve a pulsar Cancelar para descartarlos.";
      this.pinta();
      return;
    }
    if (!this.guardoAlgo && !this.recargar) {
      this.cerrado = true;
      this.dialogRef.close(undefined);
      return;
    }
    this.cierra(false);
  }

  private cierra(siguiente: boolean): void {
    if (this.cerrado) {
      return;
    }
    this.cerrado = true;
    const mensaje = this.guardoAlgo
      ? `Se guardó ${this.queSeGuardo.join(" y ")} de «${this.objetivo}».`
      : "";
    this.dialogRef.close({
      guardado: this.guardoAlgo,
      recargar: this.recargar,
      siguiente: siguiente && !this.recargar,
      mensaje,
    });
  }

  /* ============================ Utilidades ============================ */

  /** Cada tecla en un campo borra el mensaje anterior (menos el aviso de descartar). */
  cambio(): void {
    if (!this.enviando && this.mensaje && !this.recargar) {
      this.mensaje = "";
      this.confirmarDescarte = false;
    }
  }

  trackAviso(_: number, a: AvisoCaptura): string {
    return `${a.codigo}|${a.texto}`;
  }

  /** La app corre sin zonas: lo que llega de una petición se pinta a mano. */
  private pinta(): void {
    if (!this.cerrado) {
      this.cdr.detectChanges();
    }
  }

  /** Después de pintar: el campo pudo estar deshabilitado mientras se mandaba. */
  private enfoca(selector: string): void {
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(selector)?.focus());
  }
}

/* ============================ Funciones puras ============================ */

function bytes(texto: string): number {
  return new TextEncoder().encode(texto).length;
}

/** " · 5 may 2026" para «Capturó …»; vacío sin fecha. */
function sufijoFecha(fecha: string | null | undefined): string {
  return fecha ? ` · ${fechaCorta(fecha)}` : "";
}

/** "CODIGO Q2: mensaje" → { codigo, "Q2 · mensaje" }; un texto sin código se queda tal cual. */
function parteAviso(texto: string): AvisoCaptura {
  const m = FORMATO_AVISO.exec(texto ?? "");
  if (!m) {
    return { codigo: "", texto: texto ?? "" };
  }
  return { codigo: m[1], texto: m[2] ? `${m[2]} · ${m[3]}` : m[3] };
}
